"use client";
import React, { createContext, useContext, useEffect, useState } from "react";
import { useAuth } from "./AuthContext";
import { useOutlet } from "./OutletContext";
import {
  getProductsByIds,
  subscribeToProductsByIds,
  updateFavouritesArray,
} from "@/firebase/firestoreServices";
import type { Product } from "@/types";

interface FavContextType {
  favProducts: Product[];
  loading: boolean;
  isFavourite: (productId: string) => boolean;
  toggleFavourite: (product: Product) => Promise<void>;
}

const FavouritesContext = createContext<FavContextType>({
  favProducts: [],
  loading: true,
  isFavourite: () => false,
  toggleFavourite: async () => { },
});

export const useFavourites = () => useContext(FavouritesContext);

export const FavouritesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, profile } = useAuth();
  const { selectedOutlet } = useOutlet();
  const [favProducts, setFavProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);

  // Sync with profile.favourite array
  useEffect(() => {
    if (!user || !selectedOutlet?.id) {
      setFavProducts([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    const productIds = profile?.favourite || [];

    if (productIds.length === 0) {
      setFavProducts([]);
      setLoading(false);
      return;
    }

    // Subscribe to product details (for real-time stock updates)
    const unsubscribe = subscribeToProductsByIds(productIds, selectedOutlet.id, (products) => {
      setFavProducts(products);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [user, profile?.favourite, selectedOutlet?.id]);

  const isFavourite = (productId: string) => {
    return profile?.favourite?.includes(productId) || false;
  };

  const toggleFavourite = async (product: Product) => {
    if (!user) return;

    const currentIds = profile?.favourite || [];
    const isCurrentlyFav = currentIds.includes(product.id);

    try {
      if (isCurrentlyFav) {
        // Remove (Unfav)
        const newIds = currentIds.filter(id => id !== product.id);
        await updateFavouritesArray(user.uid, newIds);
      } else {
        // Add
        const newIds = [...currentIds, product.id];
        await updateFavouritesArray(user.uid, newIds);
      }
    } catch (error) {
      console.error("Error toggling favourite:", error);
    }
  };

  return (
    <FavouritesContext.Provider value={{ favProducts, loading, isFavourite, toggleFavourite }}>
      {children}
    </FavouritesContext.Provider>
  );
};
